import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import * as XLSX from 'xlsx';
import {
  Alert,
  Button,
  Card,
  Drawer,
  Icon,
  IconButton,
  Input,
  Modal,
  Select,
  Stepper,
} from 'barte-design-system';
import {
  Archive,
  LinkSimple,
  LinkBreak,
  TrashIcon,
  Plus,
  UploadSimple,
  DownloadSimple,
  CheckCircleIcon,
  CaretDown,
  WarningIcon,
  ArrowSquareOut,
} from '@phosphor-icons/react';
import './pos-shared.css';
import './DrawerOperacoesEmLote.css';
import {
  PAYFAC_OPTIONS,
  PAYFAC_NAMES,
  ALL_SELLER_OPTIONS,
  SELLER_NAMES,
} from './pos-mock-data';

// ─── Types ────────────────────────────────────────────────────────────────────

export type OperationType = 'estoque' | 'vincular' | 'desvincular' | 'firmware' | null;

export interface TerminalRow {
  serial: string;
  payfacId?: string;
  sellerId?: string;
  tipo?: 'vinculo' | 'revinculo';
  resultado?: string;
}

type TerminalModel = 'P2' | 'X990' | 'A910' | 'L300';

const TERMINAL_MODELS: TerminalModel[] = ['P2', 'X990', 'A910', 'L300'];

function detectModelFromSerial(serial: string): TerminalModel {
  const sum = serial.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return TERMINAL_MODELS[sum % TERMINAL_MODELS.length];
}

type ResultStatus = 'success' | 'warning' | 'error';

type FlowGroupType =
  | 'vinculo-payfac'
  | 'vinculo-payfac-seller'
  | 'revinculo-payfac'
  | 'revinculo-payfac-seller'
  | 'estoque'
  | 'desvinculo-seller'
  | 'desvinculo-payfac';

interface TerminalResult {
  serial: string;
  payfacId?: string;
  payfacName?: string;
  sellerId?: string;
  sellerName?: string;
  oldPayfacId?: string;
  oldPayfacName?: string;
  oldSellerId?: string;
  oldSellerName?: string;
  status: ResultStatus;
  message: string;
  errorCode?: string;
}

interface FlowGroup {
  label: string;
  type: FlowGroupType;
  results: TerminalResult[];
}

interface SellerAlloc {
  sellerId: string;
  sellerName: string;
  assignedSerials: string[];
}

export interface DrawerOperacoesEmLoteProps {
  isOpen: boolean;
  onClose: () => void;
  initialTerminals?: string[];
  initialOperation?: OperationType;
  initialStep?: number;
  initialPayfac?: string;
  autoProcess?: boolean;
  desvincularChoice?: boolean;
  payfacLocked?: string;
}

// ─── Error labels ─────────────────────────────────────────────────────────────

const ERROR_LABEL: Record<string, string> = {
  '001': 'Erro de cadastro',
  '002': 'Erro na desvinculação',
  '003': 'Erro no cadastro',
  '004': 'Erro no cadastro',
  '005': 'Erro: falta de informação',
};

// ─── Constants ────────────────────────────────────────────────────────────────

const STEPS_UPLOAD = [
  { label: 'Modelo' },
  { label: 'Terminais' },
  { label: 'Resultado' },
];

const STEPS_STANDARD = [
  { label: 'Modelo' },
  { label: 'Terminais' },
  { label: 'Operação' },
  { label: 'Resultado' },
];

const STEPS_VINCULAR = [
  { label: 'Modelo' },
  { label: 'Terminais' },
  { label: 'Operação' },
  { label: 'Vinculação' },
  { label: 'Resultado' },
];

const OPERATIONS = [
  {
    id: 'estoque' as OperationType,
    icon: Archive,
    title: 'Cadastrar no Estoque',
    description: 'Registra os terminais no estoque sem vinculação a um seller. Os terminais aparecem na listagem no status Teste.',
  },
  {
    id: 'vincular' as OperationType,
    icon: LinkSimple,
    title: 'Vinculação ao Intermediador/ Lojista',
    description: 'Vincula os terminais a Intermediadores/Lojistas. Os terminais aparecem na listagem no status Estoque Intermediador ou Alocada Lojista.',
    titleLocked: 'Vinculação ao Lojista',
    descriptionLocked: 'Vincula os terminais a Lojistas. Os terminais aparecem na listagem no status Alocada Lojista.',
  },
  {
    id: 'desvincular' as OperationType,
    icon: LinkBreak,
    title: 'Desvinculação do Intermediador/ Lojista',
    description: 'Desvincula os terminais do Intermediador/Lojista. Os terminais aparecem na listagem no status Estoque Barte ou Estoque Intermediador.',
    titleLocked: 'Desvinculação do Lojista',
    descriptionLocked: 'Desvincula os terminais do Lojista. Os terminais retornam ao estoque do Intermediador.',
  },
];

const MODEL_CARDS: { value: TerminalModel; label: string }[] = [
  { value: 'P2',   label: 'P2'   },
  { value: 'X990', label: 'X990' },
  { value: 'A910', label: 'A910' },
  { value: 'L300', label: 'L300' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function DrawerOperacoesEmLote({
  isOpen,
  onClose,
  initialTerminals,
  initialOperation,
  initialStep,
  initialPayfac,
  autoProcess,
  desvincularChoice,
  payfacLocked,
}: DrawerOperacoesEmLoteProps) {
  const [step, setStep] = useState(1);

  // Step 1 — modelo
  const [selectedModel, setSelectedModel] = useState<TerminalModel | ''>('');

  // Step 2 — terminais
  const [inputSerial, setInputSerial] = useState('');
  const [terminals, setTerminals] = useState<TerminalRow[]>([]);
  const [inputError, setInputError] = useState('');
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Step 3
  const [operation, setOperation] = useState<OperationType>(null);

  // Step 4 — config vinculação manual
  const [payfacForVinc, setPayfacForVinc] = useState('');
  const [sellerAllocs, setSellerAllocs] = useState<SellerAlloc[]>([]);
  const [openAllocDropdown, setOpenAllocDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // legado CSV
  const [payfac, setPayfac] = useState('');
  const [seller, setSeller] = useState('');

  // Resultado
  const [confirmed, setConfirmed] = useState(false);
  const [flowGroups, setFlowGroups] = useState<FlowGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [tooltip, setTooltip] = useState<{ title: string; text: string; x: number; y: number } | null>(null);

  // desvincularChoice scope
  const [desvincularScope, setDesvincularScope] = useState<'seller-only' | 'both' | null>(null);

  // autoProcess
  const [autoProcessQueued, setAutoProcessQueued] = useState(false);

  // Exit confirmation
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  // ─── Derived ─────────────────────────────────────────────────────────────────

  const isUploadMode = uploadedFileName !== null;

  // Vinculação manual = operação vincular + serials digitados (sem payfacId do CSV)
  const isVincularManual = operation === 'vincular' && !terminals.some(t => t.payfacId);

  // Step onde aparecem loading/resultados
  const resultsStep = isUploadMode ? 3 : (isVincularManual ? 5 : 4);

  // Serials já alocados a algum seller
  const assignedSerialsSet = new Set(sellerAllocs.flatMap(s => s.assignedSerials));
  const unassignedSerials = terminals.map(t => t.serial).filter(s => !assignedSerialsSet.has(s));

  // Stepper dinâmico
  const steps = isUploadMode ? STEPS_UPLOAD : (isVincularManual ? STEPS_VINCULAR : STEPS_STANDARD);

  // Inicializa o drawer com o estado recebido a cada abertura
  useEffect(() => {
    if (isOpen) {
      setSelectedModel('');
      setTerminals((initialTerminals ?? []).map((s) => ({ serial: s })));
      setOperation(initialOperation ?? null);
      setInputSerial('');
      setInputError('');
      setPayfac('');
      setSeller('');
      setPayfacForVinc(initialPayfac ?? '');
      setSellerAllocs([]);
      setOpenAllocDropdown(null);
      setPendingDelete(null);
      setUploadedFileName(null);
      setConfirmed(false);
      setFlowGroups([]);
      setDesvincularScope(null);
      if (payfacLocked) {
        setPayfacForVinc(payfacLocked);
        setSellerAllocs([]);
        setDesvincularScope('seller-only');
      }
      if (desvincularChoice) {
        setStep(4);
      } else if (autoProcess) {
        setStep(4);
        setLoading(true);
        setAutoProcessQueued(true);
      } else {
        setStep(1);
      }
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dispara o processamento automático após o estado ter sido inicializado
  useEffect(() => {
    if (!autoProcessQueued) return;
    setAutoProcessQueued(false);
    setTimeout(() => {
      setFlowGroups(buildMockResults());
      setLoading(false);
    }, 2200);
  }, [autoProcessQueued]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    if (!openAllocDropdown) return;
    function handleOutsideClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenAllocDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [openAllocDropdown]);

  // ─── Alocação de serials ──────────────────────────────────────────────────────

  function assignSerial(sellerId: string, serial: string) {
    setSellerAllocs(prev => prev.map(s =>
      s.sellerId === sellerId ? { ...s, assignedSerials: [...s.assignedSerials, serial] } : s
    ));
    setOpenAllocDropdown(null);
  }

  function unassignSerial(sellerId: string, serial: string) {
    setSellerAllocs(prev => prev.map(s =>
      s.sellerId === sellerId
        ? { ...s, assignedSerials: s.assignedSerials.filter(sr => sr !== serial) }
        : s
    ));
  }

  function removeSeller(sellerId: string) {
    setSellerAllocs(prev => prev.filter(s => s.sellerId !== sellerId));
    if (openAllocDropdown === sellerId) setOpenAllocDropdown(null);
  }

  // ─── Handlers ────────────────────────────────────────────────────────────────

  function handleAddSerial() {
    const serial = inputSerial.trim().toUpperCase();
    if (!serial) return;
    if (terminals.some((t) => t.serial === serial)) {
      setInputError('Número de série já adicionado.');
      return;
    }
    if (terminals.length >= 30) {
      setInputError('Limite de 30 terminais por vez atingido.');
      return;
    }
    setTerminals((prev) => [...prev, { serial }]);
    setInputSerial('');
    setInputError('');
    inputRef.current?.focus();
  }

  function handleInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') handleAddSerial();
  }

  function handleRemoveTerminal(serial: string) {
    setTerminals((prev) => prev.filter((t) => t.serial !== serial));
    setPendingDelete(null);
  }

  function handleFileDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  }

  async function processFile(file: File) {
    if (file.size > 3 * 1024 * 1024) {
      setInputError('O arquivo excede o tamanho máximo de 3 MB.');
      return;
    }
    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array', cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: '' });

      if (rows.length === 0) return;

      const headerKeywords = /serial|company|payfac|seller|lojista|terminal|pos/i;
      const firstRow = (rows[0] as unknown[]).map((c) => String(c ?? '').trim());
      const hasHeaders = firstRow.some((c) => headerKeywords.test(c));

      let serialIdx = 0;
      let payfacIdx = -1;
      let sellerIdx = -1;
      let startRow = 0;
      let tipoIdx = -1;
      let resultadoIdx = -1;

      if (hasHeaders) {
        const h = firstRow.map((c) => c.toLowerCase());
        serialIdx    = h.findIndex((c) => /serial|pos/.test(c));
        if (serialIdx < 0) serialIdx = 0;
        payfacIdx    = h.findIndex((c) => /payfac|company/.test(c));
        sellerIdx    = h.findIndex((c) => /seller|lojista/.test(c));
        tipoIdx      = h.findIndex((c) => /^tipo$/.test(c));
        resultadoIdx = h.findIndex((c) => /^resultado$/.test(c));
        startRow = 1;
      }

      const existingSerials = new Set(terminals.map((t) => t.serial));
      const newRows: TerminalRow[] = [];

      for (let i = startRow; i < rows.length; i++) {
        const cols = rows[i] as unknown[];
        const serial = String(cols[serialIdx] ?? '').trim().toUpperCase();
        if (!serial) continue;
        if (existingSerials.has(serial) || newRows.some((r) => r.serial === serial)) continue;

        const row: TerminalRow = { serial };

        if (payfacIdx >= 0) {
          const val = cols[payfacIdx];
          const str = val instanceof Date ? String(val.getTime()) : String(val ?? '').trim();
          if (str) row.payfacId = str;
        }
        if (sellerIdx >= 0) {
          const str = String(cols[sellerIdx] ?? '').trim();
          if (str) row.sellerId = str;
        }
        if (tipoIdx >= 0) {
          const str = String(cols[tipoIdx] ?? '').trim().toLowerCase();
          if (str === 'revinculo' || str === 'revínculo') row.tipo = 'revinculo';
          else if (str === 'vinculo' || str === 'vínculo') row.tipo = 'vinculo';
        }
        if (resultadoIdx >= 0) {
          const str = String(cols[resultadoIdx] ?? '').trim().toLowerCase();
          if (str) row.resultado = str;
        }

        newRows.push(row);
      }

      const combined = [...terminals, ...newRows];
      if (combined.length > 30) {
        setInputError('Limite de 30 terminais atingido. Apenas os primeiros 30 foram importados.');
        setTerminals(combined.slice(0, 30));
      } else {
        setTerminals(combined);
        setInputError('');
      }
      setUploadedFileName(file.name);
    } catch {
      setInputError('Não foi possível ler o arquivo. Verifique o formato e tente novamente.');
    }
  }

  function handleNext() {
    // Upload mode: step 2 → resultado (inferir operação do arquivo)
    if (isUploadMode && step === 2) {
      const detectedOp: OperationType = terminals.some(t => t.payfacId) ? 'vincular' : 'estoque';
      setOperation(detectedOp);
      setLoading(true);
      setStep(3);
      setTimeout(() => {
        setFlowGroups(buildMockResults(detectedOp));
        setLoading(false);
      }, 2200);
      return;
    }
    // Manual vincular: step 4 → resultado
    if (step === 4 && isVincularManual) {
      setLoading(true);
      setStep(5);
      setTimeout(() => {
        setFlowGroups(buildMockResults());
        setLoading(false);
      }, 2200);
      return;
    }
    if (step < 4) setStep((s) => s + 1);
  }

  function handleBack() {
    if (step > 1) setStep((s) => s - 1);
  }

  const hasProgress = selectedModel !== '' || terminals.length > 0 || operation !== null;

  function handleClose() {
    if (hasProgress && step < resultsStep) {
      setShowExitConfirm(true);
      return;
    }
    doClose();
  }

  function doClose() {
    setStep(1);
    setSelectedModel('');
    setTerminals([]);
    setInputSerial('');
    setOperation(null);
    setPayfac('');
    setSeller('');
    setPayfacForVinc('');
    setSellerAllocs([]);
    setOpenAllocDropdown(null);
    setPendingDelete(null);
    setConfirmed(false);
    setFlowGroups([]);
    setDesvincularScope(null);
    setUploadedFileName(null);
    setLoading(false);
    setTooltip(null);
    setShowExitConfirm(false);
    onClose();
  }

  function handleDownloadTemplate() {
    const rows = [
      ['serial_pos', 'payfac_id', 'seller_id', 'tipo'],
      ['SN-000001',  'PF-001',    'SEL-001',   'vinculo'],
      ['SN-000002',  'PF-001',    'SEL-002',   'revinculo'],
      ['SN-000003',  '',          '',           ''],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'template-operacao-em-lote.xlsx');
  }

  function handleDownloadErrorsCsv() {
    const errorRows = flowGroups.flatMap((g) =>
      g.results
        .filter((r) => r.status === 'error')
        .map((r) => ({
          serial_pos: r.serial,
          grupo: g.label,
          codigo_erro: r.errorCode ?? '',
          mensagem: r.message,
        })),
    );
    const ws = XLSX.utils.json_to_sheet(errorRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Erros');
    XLSX.writeFile(wb, 'erros-operacao.csv');
  }

  function handleSelectOperation(op: OperationType) {
    setOperation(op);
    const isManual = op === 'vincular' && !terminals.some(t => t.payfacId);
    if (isManual) {
      setStep(4);
      return;
    }
    setLoading(true);
    setStep(4);
    setTimeout(() => {
      setFlowGroups(buildMockResults(op));
      setLoading(false);
    }, 2200);
  }

  function buildMockResults(opOverride?: OperationType): FlowGroup[] {
    const op = opOverride ?? operation;

    const resolvePayfac = (t: TerminalRow) => ({
      payfacId:   t.payfacId ?? (payfac || undefined),
      payfacName: t.payfacId
        ? (PAYFAC_NAMES[t.payfacId] ?? 'Nome da empresa')
        : payfac
          ? (PAYFAC_OPTIONS.find((o) => o.value === payfac)?.label ?? 'Nome da empresa')
          : undefined,
    });
    const resolveSeller = (t: TerminalRow) => ({
      sellerId:   t.sellerId ?? (seller || undefined),
      sellerName: t.sellerId
        ? (SELLER_NAMES[t.sellerId] ?? 'Nome do lojista')
        : seller
          ? (ALL_SELLER_OPTIONS.find((o) => o.value === seller)?.label ?? 'Nome do lojista')
          : undefined,
    });
    const OLD_PAYFAC = { oldPayfacId: 'PF-OLD', oldPayfacName: 'Empresa Anterior' };
    const OLD_SELLER = { oldSellerId: 'S-OLD',  oldSellerName: 'Lojista Anterior'  };

    // ── Vinculação manual (step 4 de config) ──────────────────────────────────
    if (op === 'vincular' && isVincularManual) {
      const payfacName = PAYFAC_OPTIONS.find(o => o.value === payfacForVinc)?.label ?? 'Nome da empresa';
      const allResults: TerminalResult[] = [];

      sellerAllocs
        .filter(alloc => alloc.assignedSerials.length > 0)
        .forEach(alloc => {
          alloc.assignedSerials.forEach((serial, i) => {
            const base = {
              serial,
              payfacId: payfacForVinc,
              payfacName,
              sellerId: alloc.sellerId,
              sellerName: alloc.sellerName,
            };
            if (i % 5 === 3) {
              allResults.push({ ...base, status: 'error', errorCode: '003', message: 'Não foi possível cadastrar esse Lojista na Phoebus. Verifique as informações cadastrais no Cadastro da Barte e tente novamente.' });
            } else {
              allResults.push({ ...base, status: 'success', message: 'Terminal vinculado com sucesso!' });
            }
          });
        });

      return [{ label: 'Vínculo a Intermediador e Lojista', type: 'vinculo-payfac-seller', results: allResults }];
    }

    // ── Estoque ────────────────────────────────────────────────────────────────
    if (op === 'estoque') {
      return [{
        label: 'Cadastro em Lote',
        type: 'estoque',
        results: terminals.map((t, i) => {
          const base = { serial: t.serial };
          if (i % 5 === 1) return { ...base, status: 'warning', message: 'Terminal já existente no banco.' };
          if (i % 5 === 3) return { ...base, status: 'error',   message: 'Ocorreu um erro no processo de cadastro, aguarde alguns minutos e tente novamente. Se o erro persistir, entre em contato com o time de suporte.' };
          return { ...base, status: 'success', message: 'Terminal cadastrado com sucesso (status: Teste).' };
        }),
      }];
    }

    // ── Vinculação via CSV ─────────────────────────────────────────────────────
    if (op === 'vincular') {
      const hasCsvTipo = terminals.some((t) => t.tipo);
      const manualWithSeller = !hasCsvTipo && !!seller;

      const resultFromRow = (t: TerminalRow, hasSeller: boolean, isRevinc: boolean): TerminalResult => {
        const pf = resolvePayfac(t);
        const sl = hasSeller ? resolveSeller(t) : {};
        const old = isRevinc ? { ...OLD_PAYFAC, ...(hasSeller ? OLD_SELLER : {}) } : {};
        const base = { serial: t.serial, ...old, ...pf, ...sl };

        switch (t.resultado) {
          case 'aviso':    return { ...base, status: 'warning', message: 'Esse terminal já foi vinculado ao Intermediador escolhido, confira as informações.' };
          case 'erro_001': return { ...base, status: 'error', errorCode: '001', message: 'Terminal ainda não foi cadastrado no nosso estoque. Faça essa operação seguida dos testes antes de fazer uma vinculação.' };
          case 'erro_003': return { ...base, status: 'error', errorCode: '003', message: 'Não foi possível cadastrar esse Intermediador na Phoebus. Verifique as informações cadastrais no Cadastro da Barte e tente novamente.' };
          case 'erro_004': return { ...base, status: 'error', errorCode: '004', message: 'Não foi possível cadastrar esse Lojista na Phoebus. Verifique as informações cadastrais no Cadastro da Barte e tente novamente.' };
          case 'erro_005': return { ...base, status: 'error', errorCode: '005', message: 'Falta de informação: você não enviou para qual Intermediador deseja vincular esse terminal.' };
          default:         return { ...base, status: 'success', message: 'Terminal vinculado com sucesso!' };
        }
      };

      const vincPayfac:         TerminalRow[] = [];
      const vincPayfacSeller:   TerminalRow[] = [];
      const revincPayfac:       TerminalRow[] = [];
      const revincPayfacSeller: TerminalRow[] = [];

      terminals.forEach((t) => {
        const isRevinc  = t.tipo === 'revinculo';
        const hasSeller = !!(t.sellerId || manualWithSeller);
        if (isRevinc && hasSeller)        revincPayfacSeller.push(t);
        else if (isRevinc && !hasSeller)  revincPayfac.push(t);
        else if (!isRevinc && hasSeller)  vincPayfacSeller.push(t);
        else                              vincPayfac.push(t);
      });

      const groups: FlowGroup[] = [];
      if (vincPayfac.length       > 0) groups.push({ label: 'Vínculo a Intermediador',               type: 'vinculo-payfac',          results: vincPayfac.map((t)       => resultFromRow(t, false, false)) });
      if (vincPayfacSeller.length > 0) groups.push({ label: 'Vínculo a Intermediador e Lojista',     type: 'vinculo-payfac-seller',   results: vincPayfacSeller.map((t) => resultFromRow(t, true,  false)) });
      if (revincPayfac.length     > 0) groups.push({ label: 'Revínculo a Intermediador',             type: 'revinculo-payfac',        results: revincPayfac.map((t)     => resultFromRow(t, false, true))  });
      if (revincPayfacSeller.length > 0) groups.push({ label: 'Revínculo a Intermediador e Lojista', type: 'revinculo-payfac-seller', results: revincPayfacSeller.map((t) => resultFromRow(t, true, true)) });

      return groups;
    }

    // ── Desvinculação ──────────────────────────────────────────────────────────
    if (op === 'desvincular') {
      const makeDesvinc = (t: TerminalRow, i: number, incPayfac: boolean, incSeller: boolean): TerminalResult => {
        const pf = incPayfac ? resolvePayfac(t) : {};
        const sl = incSeller ? resolveSeller(t) : {};
        if (i % 4 === 2) return { serial: t.serial, ...pf, ...sl, status: 'error', errorCode: '002', message: 'Ocorreu um erro na desvinculação. Aguarde alguns minutos e tente novamente. Se o erro persistir, entre em contato com o suporte.' };
        return { serial: t.serial, ...pf, ...sl, status: 'success', message: 'Terminal desvinculado com sucesso!' };
      };

      if (desvincularScope === 'seller-only') {
        return [{
          label: 'Desvinculação do Lojista (→ Estoque Intermediador)',
          type: 'desvinculo-seller',
          results: terminals.map((t, i) => makeDesvinc(t, i, false, true)),
        }];
      }
      if (desvincularScope === 'both') {
        return [{
          label: 'Desvinculação do Intermediador e Lojista (→ Estoque Barte)',
          type: 'desvinculo-payfac',
          results: terminals.map((t, i) => makeDesvinc(t, i, true, false)),
        }];
      }

      const hasCsvSeller = terminals.some((t) => t.sellerId);
      const hasCsvPayfac = terminals.some((t) => t.payfacId);
      const showSeller   = hasCsvSeller || !!seller;
      const showPayfac   = hasCsvPayfac || !!payfac;

      const groups: FlowGroup[] = [];
      if (showSeller && showPayfac) {
        const sb: TerminalResult[] = [], pb: TerminalResult[] = [];
        terminals.forEach((t, i) => {
          if (i % 2 === 0) sb.push(makeDesvinc(t, sb.length, false, true));
          else             pb.push(makeDesvinc(t, pb.length, true, false));
        });
        if (sb.length > 0) groups.push({ label: 'Desvinculação do Lojista (→ Estoque Intermediador)', type: 'desvinculo-seller', results: sb });
        if (pb.length > 0) groups.push({ label: 'Desvinculação do Intermediador (→ Estoque Barte)',  type: 'desvinculo-payfac', results: pb });
      } else if (showSeller) {
        groups.push({ label: 'Desvinculação do Lojista (→ Estoque Intermediador)', type: 'desvinculo-seller', results: terminals.map((t, i) => makeDesvinc(t, i, false, true)) });
      } else {
        groups.push({ label: 'Desvinculação do Intermediador (→ Estoque Barte)', type: 'desvinculo-payfac', results: terminals.map((t, i) => makeDesvinc(t, i, true, false)) });
      }
      return groups;
    }

    return [];
  }

  function triggerDesvincularChoice() {
    setLoading(true);
    setTimeout(() => {
      setFlowGroups(buildMockResults());
      setLoading(false);
    }, 2200);
  }

  // ─── Footer config per step ──────────────────────────────────────────────────

  const canProceedStep2 = terminals.length > 0;
  const canProceedStep4Vinc = payfacForVinc !== '';

  const hasErrors = flowGroups.some((g) => g.results.some((r) => r.status === 'error'));

  // Choice step: desvincularChoice flow, antes de disparar o processamento
  // payfacLocked sempre usa seller-only — nunca mostra a tela de escolha
  const isDesvincularChoiceStep =
    !!(desvincularChoice && payfacLocked === undefined && step === resultsStep && !loading && !confirmed && flowGroups.length === 0);

  const currentFooter =
    isDesvincularChoiceStep ? { primaryButton: { children: 'Confirmar', onClick: triggerDesvincularChoice, disabled: !desvincularScope } } :
    step === 1 ? undefined :
    step === 2 ? { primaryButton: { children: 'Próximo', onClick: handleNext, disabled: !canProceedStep2 }, secondaryButton: { children: 'Voltar', variant: 'outline' as const, onClick: handleBack } } :
    (step === 3 && !isUploadMode && !loading && flowGroups.length === 0) ? undefined :
    (step === 4 && isVincularManual) ? { primaryButton: { children: 'Próximo', onClick: handleNext, disabled: !canProceedStep4Vinc }, secondaryButton: { children: 'Voltar', variant: 'outline' as const, onClick: handleBack } } :
    loading ? undefined :
    flowGroups.length > 0 ? {
      primaryButton: { children: 'Fechar', onClick: handleClose },
      ...(hasErrors ? { secondaryButton: { children: 'Baixar CSV com erros', variant: 'outline' as const, onClick: handleDownloadErrorsCsv } } : {}),
    } :
    undefined;

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
    {tooltip && ReactDOM.createPortal(
      <div className="olb-tooltip-portal" style={{ top: tooltip.y, left: tooltip.x }}>
        <span className="olb-tooltip-title">{tooltip.title}</span>
        <span className="olb-tooltip-text">{tooltip.text}</span>
      </div>,
      document.body,
    )}
    <Drawer
      isOpen={isOpen}
      title="Nova operação"
      onClose={handleClose}
      footer={currentFooter}
      className="olb-drawer-wide"
    >
      {/* Stepper — oculto em fluxos contextuais (autoProcess / desvincularChoice) */}
      {autoProcess !== true && desvincularChoice !== true && (
        <div className="olb-stepper">
          <Stepper steps={steps} currentStep={flowGroups.length > 0 && !loading ? steps.length + 1 : step} />
        </div>
      )}

      {step > 1 && selectedModel && (
        <div className="olb-model-chip-bar">
          <span className="olb-model-chip">Modelo: <strong>{selectedModel}</strong></span>
        </div>
      )}

      <div className="olb-body">

        {/* ── Step 1: Seleção de Modelo via cards ──────────────────────── */}
        {step === 1 && (
          <div className="olb-step">
            <div className="olb-field-group">
              <p className="olb-field-label">Modelo do terminal</p>
              <p className="olb-step-desc">
                Selecione o modelo do terminal que será operado. Só é permitido fazer operações do mesmo modelo ao mesmo tempo.
              </p>
            </div>
            <div className="olb-model-cards">
              {MODEL_CARDS.map((m) => (
                <div
                  key={m.value}
                  className={`olb-op-card${selectedModel === m.value ? ' olb-op-card--selected' : ''}`}
                  onClick={() => { setSelectedModel(m.value); setStep(2); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') { setSelectedModel(m.value); setStep(2); } }}
                >
                  <Card>
                    <div className="olb-model-card-inner">
                      <span className="olb-model-card-name">{m.label}</span>
                    </div>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Step 2: Terminais ────────────────────────────────────────── */}
        {step === 2 && (
          <div className="olb-step">

            {/* Seção A: Importar arquivo */}
            <div className="olb-field-group">
              <p className="olb-field-label">Importar arquivo</p>
              <p className="olb-step-desc">
                Envie uma planilha com os números de série e os dados de vinculação.
              </p>
            </div>

            {uploadedFileName ? (
              <div className="olb-dnd-file">
                <Icon icon={UploadSimple} size={16} weight="regular" className="olb-dnd-icon" />
                <span className="olb-dnd-filename">{uploadedFileName}</span>
                <button
                  className="olb-dnd-remove"
                  type="button"
                  aria-label="Remover arquivo"
                  onClick={() => { setUploadedFileName(null); setTerminals([]); setInputError(''); }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <div
                className="olb-dnd"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleFileDrop}
              >
                <Icon icon={UploadSimple} size={22} weight="regular" className="olb-dnd-icon" />
                <span className="olb-dnd-text">Arraste o arquivo aqui</span>
                <span className="olb-dnd-formats">máx. 3 MB · até 30 terminais · .csv / .xlsx</span>
                <label className="olb-dnd-label">
                  <span className="olb-dnd-link">Selecionar arquivo</span>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls,.numbers,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    className="olb-dnd-input"
                    onChange={handleFileChange}
                  />
                </label>
              </div>
            )}

            <div className="olb-dnd-footer">
              <button className="olb-template-btn" onClick={handleDownloadTemplate} type="button">
                <Icon icon={DownloadSimple} size={12} weight="regular" />
                Baixar template de planilha
              </button>
              <button
                type="button"
                className="olb-doc-link"
              >
                <Icon icon={ArrowSquareOut} size={12} weight="regular" />
                Documentação
              </button>
            </div>

            {/* Divisor "ou" */}
            <div className="olb-method-divider"><span>ou</span></div>

            {/* Seção B: Entrada manual */}
            <div className="olb-field-group">
              <p className="olb-field-label">Entrada manual</p>
              <p className="olb-step-desc">
                Digite ou bipe o número de série dos terminais um a um.
              </p>
            </div>

            <div className="olb-serial-row">
              <Input
                label=""
                placeholder="Ex.: SN-20240001"
                value={inputSerial}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  setInputSerial(e.target.value);
                  setInputError('');
                }}
                onKeyDown={handleInputKeyDown as React.KeyboardEventHandler<HTMLInputElement> & React.KeyboardEventHandler<HTMLTextAreaElement>}
                error={inputError || undefined}
              />
              <Button
                variant="outline"
                onClick={handleAddSerial}
                disabled={!inputSerial.trim()}
                className="olb-add-btn"
              >
                <Icon icon={Plus} size={16} weight="bold" />
                Adicionar
              </Button>
            </div>

            {/* Alert de modelos distintos */}
            {selectedModel && terminals.some(t => detectModelFromSerial(t.serial) !== selectedModel) && (
              <Alert
                variant="warning"
                title="Operação com modelos distintos"
                description="Confirme se todos os modelos dos terminais são iguais antes de seguir para o próximo passo."
                showCloseButton={false}
              />
            )}

            {/* Lista de terminais adicionados */}
            {terminals.length > 0 && (() => {
              const hasPayfacCol = terminals.some((t) => t.payfacId);
              const hasSellerCol = terminals.some((t) => t.sellerId);
              return (
                <div className="olb-preview-wrap">
                  <p className="olb-terminal-count">
                    {terminals.length} terminal{terminals.length !== 1 ? 'is' : ''} adicionado{terminals.length !== 1 ? 's' : ''}
                  </p>
                  <div className="olb-preview-table-wrap">
                    <table className="olb-preview-table">
                      <thead>
                        <tr>
                          <th className="olb-model-warn-col" />
                          <th>Id serial_pos</th>
                          {hasPayfacCol && <th>Id do Intermediador</th>}
                          {hasSellerCol && <th>Id do Lojista</th>}
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {terminals.map((row) => (
                          <tr key={row.serial}>
                            <td className="olb-model-warn-col">
                              {detectModelFromSerial(row.serial) !== selectedModel && (
                                <span
                                  className="olb-model-warn-icon"
                                  title="Essa maquininha não condiz com o modelo selecionado"
                                >
                                  <Icon icon={WarningIcon} size={14} weight="fill" />
                                </span>
                              )}
                            </td>
                            <td><span className="olb-preview-serial">{row.serial}</span></td>
                            {hasPayfacCol && (
                              <td>
                                {row.payfacId ? (
                                  <div className="olb-preview-cell">
                                    <span className="olb-preview-id">{row.payfacId}</span>
                                    <span className="olb-preview-name">{PAYFAC_NAMES[row.payfacId] ?? 'Nome da empresa'}</span>
                                  </div>
                                ) : <span className="olb-preview-empty">—</span>}
                              </td>
                            )}
                            {hasSellerCol && (
                              <td>
                                {row.sellerId ? (
                                  <div className="olb-preview-cell">
                                    <span className="olb-preview-id">{row.sellerId}</span>
                                    <span className="olb-preview-name">{SELLER_NAMES[row.sellerId] ?? 'Nome do lojista'}</span>
                                  </div>
                                ) : <span className="olb-preview-empty">—</span>}
                              </td>
                            )}
                            <td className="olb-preview-actions">
                              {pendingDelete === row.serial ? (
                                <div className="olb-delete-confirm">
                                  <span className="olb-delete-confirm-text">Remover?</span>
                                  <button className="olb-delete-yes" onClick={() => handleRemoveTerminal(row.serial)}>Sim</button>
                                  <button className="olb-delete-no" onClick={() => setPendingDelete(null)}>Não</button>
                                </div>
                              ) : (
                                <IconButton
                                  variant="ghost"
                                  size="sm"
                                  aria-label={`Remover ${row.serial}`}
                                  onClick={() => setPendingDelete(row.serial)}
                                >
                                  <Icon icon={TrashIcon} size={16} weight="regular" />
                                </IconButton>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ── Step 3: Operação (fluxo manual) ──────────────────────────── */}
        {step === 3 && !isUploadMode && (
          <div className="olb-step">
            <div className="olb-field-group">
              <p className="olb-field-label">Operação</p>
              <p className="olb-step-desc">
                Escolha o que será feito com os <strong>{terminals.length} terminal{terminals.length !== 1 ? 'is' : ''}</strong>. A operação será aplicada a todos eles.
              </p>
            </div>
            <div className="olb-op-cards">
              {(payfacLocked ? OPERATIONS.filter(op => op.id !== 'estoque') : OPERATIONS).map((op) => (
                <div
                  key={String(op.id)}
                  className={`olb-op-card${operation === op.id ? ' olb-op-card--selected' : ''}`}
                  onClick={() => handleSelectOperation(op.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleSelectOperation(op.id)}
                >
                  <Card>
                    <div className="olb-op-card-inner">
                      <div className="olb-op-icon">
                        <Icon icon={op.icon} size={24} weight="regular" />
                      </div>
                      <div>
                        <p className="olb-op-title">{payfacLocked && op.titleLocked ? op.titleLocked : op.title}</p>
                        <p className="olb-op-desc">{payfacLocked && op.descriptionLocked ? op.descriptionLocked : op.description}</p>
                      </div>
                    </div>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Step 4: Configuração de Vinculação (manual) ───────────────── */}
        {step === 4 && isVincularManual && (
          <div className="olb-step">
            <div className="olb-field-group">
              <p className="olb-field-label">Intermediador</p>
              <p className="olb-step-desc">
                Selecione o Intermediador ao qual os terminais serão vinculados.
              </p>
              <Select
                label=""
                placeholder="Selecione o intermediador"
                options={PAYFAC_OPTIONS}
                value={payfacForVinc}
                onChange={(val: string) => {
                  setPayfacForVinc(val);
                  setSellerAllocs([]);
                  setOpenAllocDropdown(null);
                }}
                hidePrefixIcon
              />
            </div>

            {payfacForVinc && (
              <>
                <div className="olb-field-group">
                  <p className="olb-field-label">Distribuição por Lojista</p>
                  <p className="olb-step-desc">
                    Terminais não alocados a nenhum Lojista ficarão no status <strong>Estoque Intermediador</strong>.
                  </p>
                  <Select
                    label=""
                    placeholder="Buscar e adicionar lojista"
                    options={ALL_SELLER_OPTIONS.filter(
                      (o) => !sellerAllocs.some((a) => a.sellerId === o.value)
                    )}
                    value=""
                    onChange={(val: string) => {
                      const opt = ALL_SELLER_OPTIONS.find((o) => o.value === val);
                      if (!opt) return;
                      setSellerAllocs((prev) => [
                        ...prev,
                        { sellerId: opt.value, sellerName: opt.label, assignedSerials: [] },
                      ]);
                    }}
                    searchable
                    filterLocally
                    hidePrefixIcon
                  />
                </div>
                <div className="olb-alloc-header">
                  <span className={`olb-alloc-counter-label${unassignedSerials.length === 0 ? ' olb-alloc-counter--ok' : ''}`}>
                    {terminals.length - unassignedSerials.length}/{terminals.length} terminais alocados
                  </span>
                  {unassignedSerials.length > 0 && (
                    <span className="olb-alloc-remaining">
                      {unassignedSerials.length} sem alocação
                    </span>
                  )}
                </div>

                <div className="olb-seller-list">
                  {sellerAllocs.map((alloc) => (
                    <div key={alloc.sellerId} className="olb-seller-row">
                      <div className="olb-seller-info">
                        <span className="olb-seller-name">{alloc.sellerName}</span>
                        <span className="olb-seller-id">{alloc.sellerId}</span>
                      </div>

                      <div className="olb-seller-chips">
                        {alloc.assignedSerials.map((serial) => (
                          <span key={serial} className="olb-serial-chip">
                            {serial}
                            <button
                              className="olb-serial-chip-remove"
                              onClick={() => unassignSerial(alloc.sellerId, serial)}
                              aria-label={`Remover ${serial}`}
                            >
                              ×
                            </button>
                          </span>
                        ))}

                        {unassignedSerials.length > 0 && (
                          <div
                            className="olb-add-terminal-wrap"
                            ref={openAllocDropdown === alloc.sellerId ? dropdownRef : null}
                          >
                            <button
                              className="olb-add-terminal-btn"
                              onClick={() => setOpenAllocDropdown(
                                openAllocDropdown === alloc.sellerId ? null : alloc.sellerId
                              )}
                            >
                              <Icon icon={Plus} size={11} weight="bold" />
                              Adicionar terminal
                              <Icon icon={CaretDown} size={11} weight="bold" />
                            </button>

                            {openAllocDropdown === alloc.sellerId && (
                              <div className="olb-add-terminal-dropdown">
                                {unassignedSerials.map((serial) => (
                                  <button
                                    key={serial}
                                    className="olb-add-terminal-item"
                                    onClick={() => assignSerial(alloc.sellerId, serial)}
                                  >
                                    {serial}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <IconButton
                        variant="ghost"
                        size="sm"
                        aria-label={`Remover ${alloc.sellerName}`}
                        onClick={() => removeSeller(alloc.sellerId)}
                        className="olb-seller-remove"
                      >
                        <Icon icon={TrashIcon} size={16} weight="regular" />
                      </IconButton>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Desvincular: escolha do tipo ─────────────────────────────── */}
        {isDesvincularChoiceStep && (
          <div className="olb-step">
            <div className="olb-field-group">
              <p className="olb-field-label">Tipo de desvinculação</p>
              <p className="olb-step-desc">
                Selecione como deseja desvincular {terminals.length === 1 ? 'o terminal selecionado' : `os ${terminals.length} terminais selecionados`}.
              </p>
            </div>
            <div className="olb-op-cards">
              <div
                className={`olb-op-card${desvincularScope === 'seller-only' ? ' olb-op-card--selected' : ''}`}
                onClick={() => setDesvincularScope('seller-only')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setDesvincularScope('seller-only')}
              >
                <Card>
                  <div className="olb-op-card-inner">
                    <div className="olb-op-icon">
                      <Icon icon={LinkBreak} size={24} weight="regular" />
                    </div>
                    <div>
                      <p className="olb-op-title">Desvincular somente do Lojista</p>
                      <p className="olb-op-desc">O terminal voltará para o status <strong>Estoque Intermediador</strong>, mantendo o vínculo com o Intermediador.</p>
                    </div>
                  </div>
                </Card>
              </div>
              <div
                className={`olb-op-card${desvincularScope === 'both' ? ' olb-op-card--selected' : ''}`}
                onClick={() => setDesvincularScope('both')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setDesvincularScope('both')}
              >
                <Card>
                  <div className="olb-op-card-inner">
                    <div className="olb-op-icon">
                      <Icon icon={Archive} size={24} weight="regular" />
                    </div>
                    <div>
                      <p className="olb-op-title">Desvincular do Intermediador e Lojista</p>
                      <p className="olb-op-desc">O terminal voltará para o status <strong>Estoque Barte</strong>, sem nenhum vínculo.</p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        )}

        {/* ── Loading ───────────────────────────────────────────────────── */}
        {step === resultsStep && loading && (
          <div className="olb-loading">
            <div className="olb-loading-spinner" />
            <div className="olb-loading-text">
              <span className="olb-loading-title">Processando lote…</span>
              <span className="olb-loading-desc">Aguarde até que as informações carreguem na tela.</span>
            </div>
          </div>
        )}

        {/* ── Confirmação final ─────────────────────────────────────────── */}
        {step === resultsStep && !loading && confirmed && (
          <div className="olb-done">
            <div className="olb-done-icon">
              <Icon icon={CheckCircleIcon} size={32} weight="regular" />
            </div>
            <div className="olb-done-text">
              <span className="olb-done-title">Operação concluída com sucesso</span>
              <span className="olb-done-desc">Acompanhe o status dos terminais na listagem.</span>
            </div>
          </div>
        )}

        {/* ── Resultado ────────────────────────────────────────────────── */}
        {step === resultsStep && !loading && !confirmed && flowGroups.length > 0 && (
          <div className="olb-step olb-step--result">
            <p className="olb-step-desc">Resultado da operação. Verifique os terminais abaixo.</p>

            {flowGroups.map((group) => {
              const successCount = group.results.filter((r) => r.status === 'success').length;
              const warningCount = group.results.filter((r) => r.status === 'warning').length;
              const errorCount   = group.results.filter((r) => r.status === 'error').length;
              const isRevinc  = group.type === 'revinculo-payfac' || group.type === 'revinculo-payfac-seller';
              const hasSeller = group.type === 'vinculo-payfac-seller' || group.type === 'revinculo-payfac-seller';

              const FeedbackCell = ({ r }: { r: TerminalResult }) => {
                const errorShortLabel = r.errorCode ? (ERROR_LABEL[r.errorCode] ?? `Erro ${r.errorCode}`) : null;
                const errorTitle = r.errorCode ? `Erro ${r.errorCode}: ${errorShortLabel}` : null;
                const badgeLabel =
                  r.status === 'success' ? 'Sucesso' :
                  r.status === 'warning' ? 'Aviso' :
                  errorShortLabel ?? `Erro ${r.errorCode ?? ''}`;

                return (
                  <div className="olb-result-feedback">
                    <span
                      className={`olb-result-badge olb-result-badge--${r.status}`}
                      onMouseEnter={(e) => {
                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        setTooltip({
                          title: errorTitle ?? (r.status === 'warning' ? 'Aviso' : 'Sucesso'),
                          text: r.message,
                          x: rect.left + rect.width / 2,
                          y: rect.top - 8,
                        });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                    >
                      <span className={`olb-result-dot olb-result-dot--${r.status}`} />
                      {badgeLabel}
                    </span>
                  </div>
                );
              };

              const IdCell = ({ id, name }: { id?: string; name?: string }) => (
                id ? (
                  <div className="olb-preview-cell">
                    <span className="olb-preview-id">{id}</span>
                    <span className="olb-preview-name">{name}</span>
                  </div>
                ) : <span className="olb-preview-empty">—</span>
              );

              return (
                <div key={group.label} className="olb-result-group">
                  <div className="olb-result-group-header">
                    <span className="olb-result-group-label">{group.label}</span>
                    <div className="olb-result-group-counts">
                      {successCount > 0 && <span className="olb-result-count olb-result-count--success">{successCount} sucesso{successCount !== 1 ? 's' : ''}</span>}
                      {warningCount > 0 && <span className="olb-result-count olb-result-count--warning">{warningCount} aviso{warningCount !== 1 ? 's' : ''}</span>}
                      {errorCount   > 0 && <span className="olb-result-count olb-result-count--error">{errorCount} erro{errorCount !== 1 ? 's' : ''}</span>}
                    </div>
                  </div>

                  <div className="olb-result-table-wrap">
                    <table className="olb-result-table">
                      <thead>
                        <tr>
                          <th>Id serial_pos</th>
                          {isRevinc ? (
                            <>
                              <th>Intermediador anterior</th>
                              <th>Intermediador novo</th>
                              {hasSeller && <th>Lojista anterior</th>}
                              {hasSeller && <th>Lojista novo</th>}
                            </>
                          ) : (
                            <>
                              <th>Intermediador</th>
                              {hasSeller && <th>Lojista</th>}
                            </>
                          )}
                          <th>Feedback</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.results.map((r) => (
                          <tr key={r.serial} className={`olb-result-row olb-result-row--${r.status}`}>
                            <td><span className="olb-preview-serial">{r.serial}</span></td>
                            {isRevinc ? (
                              <>
                                <td><IdCell id={r.oldPayfacId} name={r.oldPayfacName} /></td>
                                <td><IdCell id={r.payfacId}    name={r.payfacName}    /></td>
                                {hasSeller && <td><IdCell id={r.oldSellerId} name={r.oldSellerName} /></td>}
                                {hasSeller && <td><IdCell id={r.sellerId}    name={r.sellerName}    /></td>}
                              </>
                            ) : (
                              <>
                                <td><IdCell id={r.payfacId} name={r.payfacName} /></td>
                                {hasSeller && <td><IdCell id={r.sellerId} name={r.sellerName} /></td>}
                              </>
                            )}
                            <td><FeedbackCell r={r} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Drawer>
    <Modal
      isOpen={showExitConfirm}
      title="Deseja sair?"
      onClose={() => setShowExitConfirm(false)}
      size="sm"
      footer={{
        primaryButton: {
          children: 'Sair',
          onClick: doClose,
        },
        secondaryButton: {
          children: 'Continuar',
          variant: 'outline',
          onClick: () => setShowExitConfirm(false),
        },
      }}
    >
      <p>As ações realizadas não serão salvas. Tem certeza que deseja sair?</p>
    </Modal>
    </>
  );
}
